import { Constants } from '@/constants';
import { getTranslation } from '@/composables/data-display-utils';
import { getMaxLengthText } from '@/helpers/text.helper';
import { i18n } from '@/plugins/i18n';
import type { ConsultationDto, FormDto, Locales, VisibilityRuleSet } from '@/services/types/api.types';

// The API refuses a save that would leave the guided-consultation visibility rules invalid.
export const isInvalidVisibilityRules = (error: unknown) =>
  (error as { response?: { data?: { code?: string } } })?.response?.data?.code === 'invalid-visibility-rules';

// The rules participants follow: a consultation keeps its rules when it leaves guided mode, but nothing applies them.
export const activeRuleSets = (consultation?: ConsultationDto | null): VisibilityRuleSet[] =>
  consultation?.participation_mode === Constants.Consultation.ParticipationMode.Guided
    ? (consultation.options?.guided_consultation?.visibility?.ruleSets ?? [])
    : [];

// A condition whose source sits at or after the question it shows.
export interface RuleOrderConflict {
  key: string;
  sourceFormId: number;
  targetId: number;
}

// The conditions whose source sits at or after the question they show, by position in `forms`. A condition whose source
// or target is not in `forms` is left out, as the API leaves out a form that is not approved.
export function misorderedConditions(ruleSets: VisibilityRuleSet[], forms: FormDto[]): RuleOrderConflict[] {
  const position = new Map(
    forms.filter((form) => form.status === Constants.Form.Status.Approved).map((form, index) => [form.id, index]),
  );
  const conflicts: RuleOrderConflict[] = [];
  ruleSets.forEach((ruleSet, ruleSetIndex) => {
    const targetPosition = position.get(ruleSet.targetId);
    if (targetPosition === undefined) return;
    ruleSet.conditions.forEach((condition, conditionIndex) => {
      const sourcePosition = position.get(condition.sourceFormId);
      if (sourcePosition !== undefined && sourcePosition >= targetPosition) {
        conflicts.push({
          key: `${ruleSetIndex}:${conditionIndex}`,
          sourceFormId: condition.sourceFormId,
          targetId: ruleSet.targetId,
        });
      }
    });
  });
  return conflicts;
}

/**
 * The conditions a change of the questions' order breaks: out of order after it, in order before it. A condition
 * already out of order is not counted, so a move that repairs the order is never blocked.
 */
export function conflictsAddedBy(
  ruleSets: VisibilityRuleSet[],
  before: FormDto[],
  after: FormDto[],
): RuleOrderConflict[] {
  if (ruleSets.length === 0) return [];
  const alreadyBroken = new Set(misorderedConditions(ruleSets, before).map(({ key }) => key));
  return misorderedConditions(ruleSets, after).filter(({ key }) => !alreadyBroken.has(key));
}

/**
 * The conditions that exchanging the places of two entries of `groups` breaks.
 *
 * @param groups  in order, the forms of each sibling: one form per question of a section, or the forms of each section
 *   of a consultation, each in its own order
 */
export function swapConflicts(
  ruleSets: VisibilityRuleSet[],
  groups: FormDto[][],
  index: number,
  otherIndex: number,
): RuleOrderConflict[] {
  const swapped = [...groups];
  [swapped[index], swapped[otherIndex]] = [groups[otherIndex], groups[index]];
  return conflictsAddedBy(ruleSets, groups.flat(), swapped.flat());
}

/**
 * The conditions that moving a question to the end of another section breaks.
 *
 * @param sections  the forms of each section of the consultation, in order, each in its own order
 */
export function relocationConflicts(
  ruleSets: VisibilityRuleSet[],
  sections: FormDto[][],
  form: FormDto,
  toSectionIndex: number,
): RuleOrderConflict[] {
  const after = sections.map((forms, index) => {
    const kept = forms.filter(({ id }) => id !== form.id);
    return index === toSectionIndex ? [...kept, form] : kept;
  });
  return conflictsAddedBy(ruleSets, sections.flat(), after.flat());
}

// Same lists as SUPPORTED_SOURCE_TYPES and SUPPORTED_MANDATORY_TYPES in the API's guidedConsultationVisibilityValidator.js,
// which refuses a save that breaks them. Map questions give no discrete answer to complete, so they cannot be mandatory.
const { Type } = Constants.Form;
const SOURCE_FORM_TYPES: number[] = [Type.Question];
const MANDATORY_FORM_TYPES: number[] = [Type.Proposal, Type.Question, Type.OpenQuestion, Type.Matrix, Type.DotVoting];

export const canBeMandatory = (form: Pick<FormDto, 'type'>) => MANDATORY_FORM_TYPES.includes(form.type);

/**
 * The questions whose rules the API would refuse, judged as it judges them on a save. It first drops what refers to a
 * question that is gone (not among `forms`), as dropDanglingRules does. It then checks the rest against the approved
 * questions only, as its validator does: a target or source that is not approved, a source at or after its target, a
 * source that is not a multiple-choice question, an answer that no longer exists among the source's choices, and an
 * approved mandatory question of a type that cannot be mandatory.
 *
 * @param forms  the consultation's questions, in display order, whatever their status
 */
export function invalidFormIds(ruleSets: VisibilityRuleSet[], forms: FormDto[]): Set<number> {
  const existing = new Set(forms.map(({ id }) => id));
  const kept = ruleSets.flatMap((ruleSet) => {
    if (!existing.has(ruleSet.targetId)) return [];
    const conditions = ruleSet.conditions.filter(({ sourceFormId }) => existing.has(sourceFormId));
    return conditions.length > 0 ? [{ ...ruleSet, conditions }] : [];
  });
  const approved = new Map(
    forms.filter((form) => form.status === Constants.Form.Status.Approved).map((form) => [form.id, form]),
  );

  const invalid = new Set(misorderedConditions(kept, forms).map(({ targetId }) => targetId));
  for (const { targetId, conditions } of kept) {
    const broken =
      !approved.has(targetId) ||
      conditions.some(({ sourceFormId, valueIds = [] }) => {
        const source = approved.get(sourceFormId);
        if (!source || !SOURCE_FORM_TYPES.includes(source.type)) return true;
        const choiceIds = new Set(
          (source.answers?.steps ?? []).flatMap((step: any) => (step.choices ?? []).map((choice: any) => choice.id)),
        );
        return valueIds.some((id) => !choiceIds.has(id));
      });
    if (broken) invalid.add(targetId);
  }
  for (const form of approved.values()) {
    if (form.is_mandatory === 1 && !canBeMandatory(form)) invalid.add(form.id);
  }
  return invalid;
}

// The title of a question as the admin reads it, or `Q<id>` when the question is not among `forms` or has none.
export function formTitle(forms: FormDto[], formId: number, languageEnabled: Locales[]): string {
  const found = forms.find(({ id }) => id === formId);
  return (found && getTranslation(found.question, 'title', languageEnabled)) || `Q${formId}`;
}

const TITLE_MAX_LENGTH = 40;

/**
 * Names the questions of the first broken condition, which is enough to tell the admin what to change.
 *
 * @param key  the message that names them, as `{source}` and `{target}`
 * @param params  the other parameters of `key`
 */
export function describeConflicts(
  conflicts: RuleOrderConflict[],
  titleOf: (formId: number) => string,
  key = 'form.guided-consultation.moveBlocked',
  params: Record<string, unknown> = {},
): string {
  const [first] = conflicts;
  if (!first) return '';
  return i18n.global.t(key, {
    ...params,
    source: getMaxLengthText(titleOf(first.sourceFormId), TITLE_MAX_LENGTH),
    target: getMaxLengthText(titleOf(first.targetId), TITLE_MAX_LENGTH),
  });
}
